import type { Extension, QuestionnaireResponse } from "@medplum/fhirtypes";
import type { MemberId, QuestionnaireFlowStep } from "@repo/domain";
import {
  asPatientId,
  asQuestionnaireInstanceId,
  FlowInstance,
  parsePinnedCanonical,
  type QuestionnaireInstanceFacts,
  type QuestionnaireInstanceId,
  type QuestionnaireInstanceStatus,
  toCanonicalRef,
} from "@repo/domain";
import { extensionUrl } from "@repo/fhir-systems";
import {
  FLOW_INSTANCE_ENTITY_ID_SYSTEM,
  PATIENT_ENTITY_ID_SYSTEM,
  QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM,
} from "@repo/fhir-systems/identifiers";
import { QUESTIONNAIRE_INSTANCE_PROFILE } from "../../lib/questionnaire-response-kind";
import { PersistenceMappingError } from "../errors";
import { omitUndefined } from "../omit-undefined";
import { domainIdFromReference, referenceToIdentity } from "../references";
import { type Codec, type EncodeContext, fp } from "./codec";
import { answersFromItems, answersToItems } from "./questionnaire-answer.shared";
import { required } from "./read";

export { QUESTIONNAIRE_OPTION_SYSTEM } from "@repo/fhir-systems";
export { QUESTIONNAIRE_ANSWER_OCCURRENCE_URL } from "./questionnaire-answer.shared";

// ============================================================
// QuestionnaireInstance ⇄ FHIR QuestionnaireResponse
//
// THE PERFORMANCE MEASURE PERSISTS AS THE SAME RESOURCE TYPE. Which model a row IS stays with
// the mapper — that is a decision, not a translation — and the profile this
// codec writes on a create is the marker it decides on. The session ANDs the
// discriminator into every find and checks it on every get, so no caller can
// produce the wrong kind by forgetting.
//
// THE PIN LIVES IN `questionnaire` AS `url|version`. Medplum registers that
// element as a REFERENCE search parameter rather than a uri, so a pinned value
// does not match a bare-url search: "every response to this lineage" is not an
// indexed query and is done by searching on patient and filtering in memory.
//
// ANSWERS ARE FLAT ON BOTH SIDES, which is a deliberate departure from
// "rebuild the nested shape on write": encoding is handed the prior row and a
// naming context, never the definition, so on a CREATE there is nothing to
// rebuild the nesting from. An item per answered address, with the repetition
// carried in an extension, is reconstructible from entity state alone and
// round-trips exactly. Rebuilding true section/group nesting needs the
// definition at encoding time, which is an engine change, not a mapper one.
// ============================================================

/**
 * `underAmendment` has no code in FHIR's fixed `QuestionnaireResponse.status`
 * value set, so the row carries `in-progress` plus this marker. External
 * consumers see a reopened record as in progress, which is honest; the
 * separate completion timestamp preserves when it was last filed.
 */
export const QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL = extensionUrl("questionnaire-instance-under-amendment");
export const QUESTIONNAIRE_INSTANCE_AMENDMENT_URL = extensionUrl("questionnaire-instance-amendment");
export const QUESTIONNAIRE_INSTANCE_COMPLETED_AT_URL = extensionUrl("questionnaire-instance-completed-at");

/**
 * The flow run that assigned this instance and the node it answers: a complex
 * extension holding a reference to the run's RequestGroup and the node id.
 */
export const QUESTIONNAIRE_INSTANCE_FLOW_STEP_URL = extensionUrl("questionnaire-instance-flow-step");

function flowStepFromFhir(row: QuestionnaireResponse): QuestionnaireFlowStep | undefined {
  const step = row.extension?.find((e) => e.url === QUESTIONNAIRE_INSTANCE_FLOW_STEP_URL);
  if (!step) return undefined;
  const part = (url: string) => step.extension?.find((e) => e.url === url);
  return {
    flowInstanceId: FlowInstance.asId(
      required(
        row,
        "flowStep.flowInstance",
        domainIdFromReference(FLOW_INSTANCE_ENTITY_ID_SYSTEM, part("flowInstance")?.valueReference),
      ),
    ),
    nodeId: required(row, "flowStep.node", part("node")?.valueString) as MemberId,
  };
}

function flowStepToFhir(step: QuestionnaireFlowStep, ctx: EncodeContext | undefined): Extension {
  return {
    url: QUESTIONNAIRE_INSTANCE_FLOW_STEP_URL,
    extension: [
      {
        url: "flowInstance",
        valueReference: referenceToIdentity(ctx, "RequestGroup", FLOW_INSTANCE_ENTITY_ID_SYSTEM, step.flowInstanceId),
      },
      { url: "node", valueString: step.nodeId },
    ],
  };
}

export function questionnaireInstanceDomainId(response: QuestionnaireResponse): QuestionnaireInstanceId {
  const identifier = response.identifier;
  if (identifier?.system !== QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM || !identifier.value) {
    const locator = response.id === undefined ? "<unknown>" : response.id;
    throw new PersistenceMappingError(
      `FHIR QuestionnaireResponse/${locator} has no populated domain identifier for '${QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM}'`,
    );
  }
  return asQuestionnaireInstanceId(identifier.value);
}

export const QUESTIONNAIRE_INSTANCE_TO_FHIR_STATUS: Record<
  QuestionnaireInstanceStatus,
  QuestionnaireResponse["status"]
> = {
  inProgress: "in-progress",
  underAmendment: "in-progress",
  completed: "completed",
  amended: "amended",
  stopped: "stopped",
  enteredInError: "entered-in-error",
};

function isUnderAmendment(extensions: Extension[] | undefined): boolean {
  return (
    extensions?.some((e) => e.url === QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL && e.valueBoolean === true) === true
  );
}

/**
 * `underAmendment` rides as `in-progress` plus the marker, and a row carrying
 * a status this model has no code for reads as in progress — which is what an
 * unfinished record is. Spelled as a switch, the same shape the exam uses, so
 * the two models that share this resource type also share one reading of it.
 */
function statusFromFhir(response: QuestionnaireResponse): QuestionnaireInstanceStatus {
  switch (response.status) {
    case "completed":
      return "completed";
    case "amended":
      return "amended";
    case "stopped":
      return "stopped";
    case "entered-in-error":
      return "enteredInError";
    default:
      return isUnderAmendment(response.extension) ? "underAmendment" : "inProgress";
  }
}

/**
 * The instance's own extensions, merged over whatever else is stored. `extension`
 * is one FHIR field, so writing only ours would drop every extension another
 * writer put there.
 */
function instanceExtensions(
  prior: Extension[] | undefined,
  underAmendment: boolean,
  amendment: QuestionnaireInstanceFacts["amendment"],
  completedAt: string | undefined,
  flowStep: QuestionnaireFlowStep | undefined,
  ctx: EncodeContext | undefined,
): Extension[] | undefined {
  const ours = new Set<string>([
    QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL,
    QUESTIONNAIRE_INSTANCE_AMENDMENT_URL,
    QUESTIONNAIRE_INSTANCE_COMPLETED_AT_URL,
    QUESTIONNAIRE_INSTANCE_FLOW_STEP_URL,
  ]);
  const others = (prior ?? []).filter((e) => !ours.has(e.url));
  const merged = [
    ...others,
    ...(amendment ? [{ url: QUESTIONNAIRE_INSTANCE_AMENDMENT_URL, valueString: JSON.stringify(amendment) }] : []),
    ...(underAmendment ? [{ url: QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL, valueBoolean: true }] : []),
    ...(completedAt ? [{ url: QUESTIONNAIRE_INSTANCE_COMPLETED_AT_URL, valueDateTime: completedAt }] : []),
    ...(flowStep ? [flowStepToFhir(flowStep, ctx)] : []),
  ];
  return merged.length > 0 ? merged : undefined;
}

function amendmentFromFhir(response: QuestionnaireResponse): QuestionnaireInstanceFacts["amendment"] {
  const encoded = response.extension?.find((e) => e.url === QUESTIONNAIRE_INSTANCE_AMENDMENT_URL)?.valueString;
  if (encoded === undefined) return undefined;
  try {
    const value = JSON.parse(encoded);
    if (!value || !Array.isArray(value.baseline) || typeof value.reason !== "string" || !value.reason.trim()) {
      throw new Error("Invalid amendment state");
    }
    return value;
  } catch {
    throw new PersistenceMappingError("Questionnaire instance has malformed amendment state");
  }
}

function decode(response: QuestionnaireResponse): QuestionnaireInstanceFacts {
  return omitUndefined({
    id: questionnaireInstanceDomainId(response),
    definition: required(response, "definition", parsePinnedCanonical(response.questionnaire)),
    patientId: asPatientId(
      required(response, "patientId", domainIdFromReference(PATIENT_ENTITY_ID_SYSTEM, response.subject)),
    ),
    assignedAt: required(response, "assignedAt", response.authored),
    status: statusFromFhir(response),
    completedAt: response.extension?.find((e) => e.url === QUESTIONNAIRE_INSTANCE_COMPLETED_AT_URL)?.valueDateTime,
    answers: answersFromItems(response, response.item),
    amendment: amendmentFromFhir(response),
    flowStep: flowStepFromFhir(response),
  });
}

function encode(
  facts: QuestionnaireInstanceFacts,
  prior: QuestionnaireResponse | null,
  ctx?: EncodeContext,
): QuestionnaireResponse {
  const items = answersToItems(facts.answers, prior?.item);
  return omitUndefined({
    resourceType: "QuestionnaireResponse" as const,
    id: prior?.id,
    identifier: { system: QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM, value: facts.id },
    meta: prior?.meta ?? { profile: [QUESTIONNAIRE_INSTANCE_PROFILE] },
    status: QUESTIONNAIRE_INSTANCE_TO_FHIR_STATUS[facts.status],
    subject: referenceToIdentity(ctx, "Patient", PATIENT_ENTITY_ID_SYSTEM, facts.patientId),
    questionnaire: toCanonicalRef(facts.definition),
    authored: facts.assignedAt,
    // Undefined rather than `[]` when there is nothing answered: `item` is an
    // owned field, so absent is what CLEARS it.
    item: items.length > 0 ? items : undefined,
    extension: instanceExtensions(
      prior?.extension,
      facts.status === "underAmendment",
      facts.amendment,
      facts.completedAt,
      facts.flowStep,
      ctx,
    ),
  });
}

export const questionnaireInstanceCodec: Codec<
  QuestionnaireInstanceFacts,
  keyof QuestionnaireInstanceFacts,
  QuestionnaireResponse
> = {
  resourceType: "QuestionnaireResponse",
  reads: {
    id: [fp(`identifier.where(system = '${QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM}').value`)],
    definition: [fp("questionnaire")],
    patientId: [fp("subject")],
    assignedAt: [fp("authored")],
    status: [fp("status"), fp(`extension.where(url = '${QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL}').valueBoolean`)],
    completedAt: [fp(`extension.where(url = '${QUESTIONNAIRE_INSTANCE_COMPLETED_AT_URL}').valueDateTime`)],
    answers: [fp("item")],
    amendment: [fp(`extension.where(url = '${QUESTIONNAIRE_INSTANCE_AMENDMENT_URL}').valueString`)],
    flowStep: [fp(`extension.where(url = '${QUESTIONNAIRE_INSTANCE_FLOW_STEP_URL}')`)],
  },
  // `item` is owned: clearing the last answer must reach the store. Under
  // merge-over-prior an absent key means "leave it alone", so an emptied list
  // would diff as no change and the commit would report success having sent
  // nothing. `authored` and `questionnaire` are owned for the same reason.
  owns: [fp("item"), fp("authored"), fp("questionnaire")],
  // Read without hydrating, for the QuestionnaireCompleted detector.
  fieldReaders: { id: questionnaireInstanceDomainId, status: statusFromFhir },
  decode,
  encode,
};
